import { CATALOG as TRIP_DOC_CATALOG, type TranslateFn } from '@voll-intelligence/trip-document';
export { type TranslateFn } from '@voll-intelligence/trip-document';
import { CATALOG } from './catalog.js';

export type TripPanelLocale = keyof typeof CATALOG;

export interface TripPanelI18n {
  t: TranslateFn;
  currentLocale: () => string;
}

/** Fallback sem host: os dois catálogos fundidos, plural `_one/_other` por
 *  `Intl.PluralRules` e interpolação `{{var}}` — os mesmos dois comportamentos do
 *  i18next de que os arquivos movidos dependem (espelho do `createTranslator` do
 *  trip-document, que só conhece o catálogo dele). */
function fallbackTranslator(locale: TripPanelLocale): TranslateFn {
  const table: Record<string, string> = { ...TRIP_DOC_CATALOG[locale], ...CATALOG[locale] };
  const plural = new Intl.PluralRules(locale);
  return (key, vars) => {
    let template = table[key];
    if (vars && typeof vars['count'] === 'number') {
      template =
        table[`${key}_${plural.select(vars['count'])}`] ?? table[`${key}_other`] ?? template;
    }
    if (template == null) return key;
    return template.replaceAll(/\{\{(\w+)\}\}/g, (_, name: string) => {
      const value = vars?.[name];
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      return '';
    });
  };
}

const FALLBACK: TripPanelI18n = { t: fallbackTranslator('pt-BR'), currentLocale: () => 'pt-BR' };
let active: TripPanelI18n = FALLBACK;

/** O host (web-chat, web) liga o próprio i18n uma vez no boot. */
export function configureTripPanelI18n(i18n: TripPanelI18n): void {
  active = i18n;
}

/** Seam de teste: volta ao fallback. */
export function resetTripPanelI18n(): void {
  active = FALLBACK;
}

/** A chave é `string` frouxa de propósito: o pacote não conhece o `ParseKeys` do host;
 *  `catalog.test.ts` cobre as chaves literais e o fallback devolve a chave crua. */
export function t(key: string, vars?: Record<string, unknown>): string {
  return active.t(key, vars);
}

export function currentLocale(): string {
  return active.currentLocale();
}
